/* eslint-disable sort-keys-fix/sort-keys-fix */

/*  Shared  */
import { Colors } from '@auviknetworks/iceberg-ui';
import { FontSizes, SIDEBAR_WIDTH } from 'application/Shared/Constants';

import { OwnerState } from './muiTypes';

const {
    auvikBrown,
    auvikCyan,
    auvikEggplant,
    auvikGreen,
    auvikLavender,
    auvikLime,
    auvikOrange,
    auvikPink,
    auvikPurple,
    auvikRed,
    auvikSkyBlue,
    auvikYellow,
    black,
    eggplantGray,
    gray,
    lavenderGray,
    lightGray,
    white
} = Colors;

/*  Styles  */
export const defaultDarkTheme2023 = {
    palette: {
        accountsReport: {
            chipType: {
                dormant: {
                    backgroundColor: auvikRed[500],
                    borderColor: auvikRed[500],
                    textColor: black
                },
                active: {
                    backgroundColor: auvikGreen[500],
                    borderColor: auvikGreen[500],
                    textColor: black
                }
            }
        },
        alert: {
            text: {
                info: auvikEggplant[500],
                success: auvikEggplant[500],
                warning: auvikEggplant[500],
                error: auvikEggplant[500]
            }
        },
        anmTrial: {
            backgroundColorPrimary: black,
            backgroundColorSecondary: eggplantGray[800],
            errorScreen: {
                backgroundColor: auvikYellow[500],
                borderColor: auvikYellow[500],
                textColor: auvikEggplant[500]
            },
            linkColor: auvikPurple[500],
            primaryTextColor: white,
            titleTextColor: auvikLavender[500]
        },
        avatar: {
            appsAvatar: { backgroundColor: eggplantGray[700] },
            appsAvatarGroup: { backgroundColor: auvikCyan[500] },
            sso: { backgroundColor: auvikPurple[100], color: black },
            ssoSecondary: {
                backgroundColor: auvikGreen[500]
            },
            text: { white, black }
        },
        background: {
            avatar: auvikCyan[500],
            calendarSelectedSection: auvikPurple[800],
            configureIntegration: eggplantGray[800],
            contractSectionTitle: eggplantGray[800],
            contractsReport: eggplantGray[800],
            contractSummaryCardWhite: auvikPurple[800],
            contractTable: black,
            contractTableLight: eggplantGray[800],
            danger: auvikRed[500],
            integrationMethodIcon: black,
            login: black,
            loginGraphic: auvikEggplant[500],
            optionIntegration: black,
            pageNotFound: auvikEggplant[800],
            primary: black,
            primaryLight: eggplantGray[800],
            reports: black,
            reportSummaryCardDark: auvikPurple[500],
            reportSummaryCardLight: auvikPurple[900],
            saasOpsUserDetails: black,
            secondary: lavenderGray[50],
            tertiary: eggplantGray[800],
            timePeriodPicker: eggplantGray[800],
            warning: auvikOrange[500],
            white
        },
        bigCalendar: {
            monthly: {
                background: auvikGreen[500],
                border: auvikGreen[500],
                icon: black
            },
            renew: {
                background: auvikCyan[500],
                border: auvikCyan[500],
                icon: black
            },
            agendaTableBorderColor: gray[50],
            allDayCellBorderColor: gray[50],
            appName: black,
            backgroundEvent: white,
            backgroundEventFocusOutline: auvikLavender[800],
            backgroundEventSelectedBackground: auvikLavender[800],
            buttonLink: white,
            buttonLinkHover: auvikLavender[500],
            currentTimeIndicatorBackgroundColor: white,
            dayBgBorderColor: lavenderGray[500],
            daySlotBackgroundEventBorderColor: auvikLavender[900],
            headerBackgroundColor: eggplantGray[800],
            headerColor: white,
            monthRowBorderColor: lavenderGray[500],
            monthViewBorderColor: lavenderGray[500],
            offRange: gray[100],
            overflowingBorderColor: gray[50],
            overlayBackgroundColor: white,
            overlayBorderColor: lightGray[500],
            overlayHeaderBorderColor: lightGray[500],
            timeContentBorderColor: gray[50],
            timeHeaderBorderColor: gray[50],
            timeHeaderGutterBackgroundColor: white,
            timeHeaderGutterBorderColor: gray[50],
            timeSlotBorderColor: lavenderGray[50],
            timeSlotGroupBorderColor: gray[50],
            timeViewBorderColor: gray[50],
            todayBackgroundColor: auvikEggplant[100],
            toolbarButtonActiveBackgroundColor: auvikLavender[800],
            toolbarButtonActiveBorderColor: white,
            toolbarButtonActiveHoverBackgroundColor: auvikLavender[800],
            toolbarButtonActiveHoverBorderColor: white,
            toolbarButtonActiveHoverColor: white,
            toolbarButtonBorderColor: white,
            toolbarButtonColor: white,
            toolbarButtonHoverBackgroundColor: auvikLavender[500],
            toolbarButtonHoverBorderColor: white,
            toolbarButtonHoverColor: white,
            toolbarLabel: white
        },
        border: {
            appBubbles: black,
            card: black,
            divider: auvikPurple[900],
            darkerDivider: lightGray[500],
            pageHeader: black,
            pageNotFound: white,
            primary: white,
            secondary: auvikPurple[800],
            tertiary: auvikPurple[500],
            highlightedCard: black,
            userDetailsAppHeadlineCard: black
        },
        breach: {
            backgroundColor: { safe: auvikGreen[500], critical: auvikRed[500] },
            borderColor: {
                safe: auvikGreen[500],
                critical: auvikRed[500]
            },
            textColor: {
                default: black,
                safe: auvikGreen[500],
                critical: auvikRed[500]
            }
        },
        button: {
            primary: auvikLavender[800],
            cancel: auvikRed[500],
            danger: auvikRed[500],
            success: auvikGreen[500],
            warning: auvikRed[500],
            white
        },
        calendar: { monthly: auvikGreen[500], renew: auvikCyan[500] },
        chart: {
            axis: white,
            axisTickValue: white,
            axisLine: white,
            axisTickLine: white,
            bar: auvikSkyBlue[500],
            line: auvikYellow[500],
            saasHealthLineGraph: auvikYellow[500],
            tooltipBackground: black,
            tooltipObjectBackground: eggplantGray[700],
            primary: auvikLavender[500],
            secondary: auvikPurple[500],
            tertiary: auvikCyan[500],
            danger: auvikRed[500],
            success: auvikGreen[500],
            warning: auvikOrange[500]
        },
        checkbox: {
            border: { primary: white },
            checked: {
                primary: auvikLavender[800],
                secondary: auvikPurple[500],
                tertiary: auvikLavender[800]
            }
        },
        chip: {
            background: {
                primary: auvikCyan[500],
                secondary: auvikLavender[500],
                tertiary: auvikPurple[500]
            },
            text: {
                primary: black,
                secondary: white
            }
        },
        community: {
            likeBadge: {
                likedBackgroundColor: auvikLavender[800],
                unlikedBackgroundColor: white,
                borderColor: auvikLavender[800]
            }
        },
        confetti: {
            color1: '#F1EAF3',
            color2: '#DBCAE2',
            color3: auvikEggplant[500],
            color4: '#6A3583',
            color5: '#572572',
            color6: '#BEBDD0',
            color7: auvikPurple[500],
            color8: '#171446'
        },
        contract: {
            chipType: {
                month: {
                    backgroundColor: auvikGreen[500],
                    borderColor: auvikGreen[500],
                    iconColor: black
                },
                renew: {
                    backgroundColor: auvikCyan[500],
                    borderColor: auvikCyan[500],
                    iconColor: black
                }
            }
        },
        customizedDot: { color: white, border: white },
        danger: { main: auvikRed[500] },
        error: { main: auvikRed[500] },
        externalAppStatus: {
            critical: auvikRed[500],
            major: auvikOrange[100],
            minor: auvikYellow[500],
            none: auvikGreen[500],
            resolved: auvikGreen[500]
        },
        fileBrowser: {
            borderColor: black,
            linkColor: auvikPurple[500],
            borderHighlight: auvikPurple[500],
            itemColor: white,
            itemBackgroundColor: auvikPurple[500],
            tableHover: auvikPurple[500],
            tableLink: white,
            fileBrowser: black,
            fileBrowserSelected: auvikPurple[500]
        },
        graph: {
            axisLabels: lightGray[200],
            colors: [
                auvikSkyBlue[900],
                auvikCyan[500],
                auvikYellow[100],
                auvikPink[500],
                auvikOrange[100],
                auvikGreen[100],
                auvikPurple[500],
                auvikLime[900],
                auvikCyan[800],
                auvikPurple[50]
            ]
        },
        hover: {
            primary: auvikLavender[900],
            secondary: auvikEggplant[500],
            tertiary: auvikPurple[500]
        },
        iceberg: {
            colorAction: auvikLavender[800],
            colorBrandPrimary: auvikPurple[500],
            colorBrandSecondary: auvikLavender[500]
        },
        icebergForm: {
            formLabelColor: white,
            formLabelColorError: auvikRed[500],
            textFieldInputBackgroundColorReadOnly: eggplantGray[700],
            textFieldInputBackgroundColor: eggplantGray[700],
            textFieldInputColor: lightGray[200],
            textFieldInputColorReadOnly: gray[300],
            textFieldOutline: eggplantGray[700],
            textFieldOutlineError: auvikRed[500],
            textFieldOutlineFocused: auvikSkyBlue[500],
            selectBackgroundColor: eggplantGray[700],
            selectOutline: auvikSkyBlue[500],
            chevronIcon: white
        },
        icon: {
            acceptSave: auvikGreen[500],
            accordionExpand: white,
            accordionItem: white,
            avatar: black,
            badgeWarning: auvikYellow[500],
            cancel: auvikRed[500],
            clientDefault: auvikLavender[500],
            danger: auvikRed[500],
            disabled: eggplantGray[400],
            discoveredByQuickScan: auvikGreen[500],
            error: auvikRed[500],
            info: auvikCyan[800],
            infoDark: auvikSkyBlue[800],
            managedByPartner: auvikLavender[500],
            primary: white,
            search: lightGray[200],
            success: auvikGreen[500],
            tertiary: auvikPurple[500],
            textColor: white,
            warning: auvikOrange[500],
            white
        },
        info: { main: auvikCyan[800] },
        lifecycleStages: {
            background: {
                unapproved: auvikRed[500],
                notConfigured: auvikPurple[50],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: gray[50],
                decommissioned: auvikCyan[100]
            },
            borderColor: {
                unapproved: auvikRed[500],
                notConfigured: auvikPurple[50],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: gray[50],
                decommissioned: auvikCyan[100]
            },
            icon: {
                unapproved: black,
                notConfigured: black,
                evaluating: white,
                implementing: black,
                approved: black,
                decommissioning: black,
                decommissioned: black
            },
            iconFilter: {
                unapproved: auvikRed[500],
                notConfigured: auvikPurple[50],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: gray[50],
                decommissioned: auvikCyan[100]
            },
            textColor: {
                unapproved: black,
                notConfigured: black,
                evaluating: white,
                implementing: black,
                approved: black,
                decommissioning: black,
                decommissioned: black
            }
        },
        managedPicker: {
            background: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[500]
            },
            borderColor: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[900]
            },
            icon: {
                managed: black,
                unmanaged: black,
                unclassified: black
            },
            iconFilter: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[500]
            },
            textColor: {
                managed: black,
                unmanaged: black,
                unclassified: black
            }
        },
        menuItem: {
            hoverBackgroundColor: auvikPurple[900]
        },
        metadata: { background: gray[800] },
        modal: {
            backgroundColor: {
                footer: eggplantGray[800],
                title: auvikEggplant[500]
            },
            text: {
                titleTextPrimary: white
            },
            card: {
                backgroundColor: black
            }
        },
        notes: {
            icon: {
                client: auvikCyan[500],
                community: auvikLavender[100],
                open: auvikPurple[500],
                partner: auvikLavender[500]
            }
        },
        onboarding: {
            backgroundColorPrimary: black,
            backgroundColorSecondary: eggplantGray[800],
            backgroundColorGray: eggplantGray[800],
            backgroundColorWhite: white,
            bonusConfigurationBadgeBackgroundColor: auvikLavender[900],
            buttonPrimary: auvikLavender[800],
            cardBodyText: white,
            checklistBackgroundColor: black,
            deployAgentInsteadGraphic: {
                backgroundColor: {
                    light: lavenderGray[100],
                    medium: lavenderGray[300],
                    dark: lavenderGray[500]
                }
            },
            integrationCardBackgroundColor: black,
            integrationCardBorderColor: black,
            primaryTextColor: white,
            quickScanChooseCardBackgroundColor: black,
            tertiaryIconColor: auvikLavender[500],
            tertiaryTextColor: white,
            titleTextColor: auvikLavender[500]
        },
        pieChart: {
            personal: auvikYellow[500],
            domain: auvikPurple[900],
            na: auvikLavender[100]
        },
        primary: { main: white },
        progressBar: {
            backgroundColor: eggplantGray[800],
            main: auvikPurple[500]
        },
        providerGroup: {
            default: {
                borderColor: auvikSkyBlue[900],
                backgroundColor: auvikSkyBlue[900],
                text: white
            },
            orgUnit: {
                borderColor: auvikPurple[500],
                backgroundColor: auvikPurple[500],
                text: white
            }
        },
        qbrFirstReports: {
            AppClassificationScore: auvikPurple[50],
            ManagedAppScore: auvikCyan[500],
            ShadowITScore: auvikYellow[100],
            UnapprovedAppScore: auvikPink[500],
            AppOverlapScore: auvikPurple[500],
            SaaSSophisticationScore: auvikLavender[500],
            scoreTextColor: {
                increase: auvikGreen[500],
                decrease: auvikRed[500],
                unchanged: white
            }
        },
        reports: {
            v2Header: {
                boxShadow: 'none'
            }
        },
        risk: {
            backgroundColor: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[50],
                safe: auvikCyan[500],
                undefined: gray[50]
            },
            borderColor: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[50],
                safe: auvikCyan[500],
                undefined: gray[50]
            },
            icon: {
                critical: black,
                high: black,
                medium: black,
                low: black,
                alert: black,
                safe: black,
                undefined: black
            },
            iconFilter: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[50],
                safe: auvikCyan[500],
                undefined: gray[50]
            },
            pieChartColor: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                default: auvikEggplant[500]
            },
            textColor: {
                critical: black,
                high: black,
                medium: black,
                low: black,
                alert: black,
                safe: black,
                undefined: black
            }
        },
        saasScore: {
            borderColor: {
                excellent: auvikGreen[900],
                medium: auvikOrange[900],
                low: auvikRed[900],
                none: black
            },
            fillColor: {
                excellent: auvikGreen[500],
                medium: auvikOrange[500],
                low: auvikRed[500],
                none: gray[500]
            },
            unfilledColor: {
                excellent: auvikGreen[50],
                medium: auvikOrange[50],
                low: auvikRed[50],
                none: gray[50]
            },
            text: {
                excellent: auvikGreen[500]
            }
        },
        saasOpsAppList: {
            backgroundColor: {
                hover: auvikPurple[900],
                current: auvikPurple[900]
            },
            borderBottom: black,
            trash: {
                backgroundColor: eggplantGray[800]
            }
        },
        saasOpsCard: {
            backgroundColor: black
        },
        saasOpsState: {
            background: {
                integrated: auvikGreen[500],
                error: auvikRed[500]
            },
            borderColor: {
                integrated: auvikGreen[500],
                error: auvikRed[500]
            },
            icon: {
                integrated: black,
                error: black
            },
            textColor: {
                integrated: black
            }
        },
        scripts: {
            color: white,
            backgroundColor: auvikEggplant[500],
            borderColor: auvikEggplant[500]
        },
        sidebar: {
            backgroundColor: lavenderGray[800],
            backgroundColorBottomSection: lavenderGray[900],
            clientTitle: white,
            clientName: white,
            divider: lavenderGray[800],
            text: white,
            icon: white,
            itemSelected: {
                backgroundColor: auvikPurple[500],
                hover: {
                    backgroundColor: auvikPurple[500]
                }
            },
            item: {
                hover: {
                    backgroundColor: lavenderGray[900]
                },
                bottomItemHover: {
                    backgroundColor: lavenderGray[800]
                }
            },
            newBadge: {
                selected: lavenderGray[800],
                unselected: auvikPurple[500]
            },
            mainTabSelected: {
                backgroundColor: auvikPurple[500],
                hover: {
                    backgroundColor: auvikPurple[500]
                }
            },
            mainTab: {
                hover: {
                    backgroundColor: lavenderGray[900]
                },
                color: white
            },
            subTabSelected: {
                borderLeftColor: white,
                hover: {
                    backgroundColor: lavenderGray[800]
                }
            },
            subTab: {
                hover: {
                    backgroundColor: lavenderGray[900]
                },
                color: lavenderGray[100]
            }
        },
        secondary: { main: auvikLavender[500] },
        sparkLines: {
            sparkLine: auvikYellow[500],
            sparkBar: auvikSkyBlue[500],
            stroke: auvikSkyBlue[500]
        },
        ssoSupport: {
            globalSupport: {
                iconPrint: eggplantGray[500],
                background: lavenderGray[500]
            },
            tenantUse: {
                iconPrint: auvikGreen[500],
                background: auvikGreen[500]
            },
            tooltip: {
                divider: eggplantGray[800]
            }
        },
        success: { main: auvikGreen[500] },
        switchAuvik: {
            baseChecked: white,
            baseTrackBackground: auvikPurple[500],
            baseFocusBorder: white,
            baseFocusColor: auvikPurple[500],
            baseDisabled: lavenderGray[700],
            thumbBackground: white,
            thumbColor: white,
            trackBackground: gray[300]
        },
        tab: { divider: eggplantGray[800], subdivider: eggplantGray[600] },
        table: {
            border: eggplantGray[800],
            dangerRowBackground: auvikRed[500],
            dangerRowText: black,
            evenRowBackground: eggplantGray[800],
            emptyRowsBackground: eggplantGray[800],
            footerBackground: eggplantGray[800],
            headerRowBackground: black,
            headerBorderSortable: auvikPurple[500],
            oddRowBackground: black,
            rowBackground: eggplantGray[800],
            subtableBackground: eggplantGray[700],
            subtableHeader: black,
            successRowBackground: auvikGreen[500],
            successRowText: black,
            totalRowBackground: eggplantGray[700]
        },
        tag: {
            option1: auvikCyan[500],
            option2: auvikLavender[500],
            option3: auvikOrange[500],
            option4: auvikGreen[500],
            option5: auvikBrown[500],
            option6: auvikPink[500],
            option7: auvikYellow[800],
            option8: auvikSkyBlue[500]
        },
        tenantSelector: {
            input: {
                backgroundColor: black,
                borderColor: black
            },
            menuListColor: white,
            paperProps: {
                item: {
                    color: white,
                    hover: {
                        backgroundColor: lavenderGray[800],
                        color: white
                    }
                },
                itemSelected: {
                    backgroundColor: auvikPurple[500],
                    color: white,
                    hover: {
                        backgroundColor: auvikPurple[500]
                    }
                },
                itemSelectedHover: {
                    backgroundColor: auvikPurple[500],
                    color: white
                },
                backgroundColor: black,
                borderColor: black
            }
        },
        tertiary: { main: auvikPurple[500] },
        text: {
            calendarSelectedSection: auvikPurple[50],
            calendarSelectedSectionDisabled: auvikPurple[800],
            cardKPI: white,
            cardKPINoData: lightGray[200],
            danger: auvikRed[500],
            disabled: eggplantGray[400],
            emailAddress: auvikLavender[500],
            error: auvikRed[500],
            link: { text: white, hover: auvikPurple[500] },
            pageNotFound: white,
            noData: lightGray[200],
            overlappingAppsCount: white,
            primary: white,
            primaryFaded: auvikPurple[50],
            reportSummaryCardDark: white,
            reportSummaryCardLight: white,
            secondary: white,
            success: auvikGreen[500],
            tag: { primary: gray[700] },
            tertiary: auvikPurple[500],
            warning: auvikBrown[500],
            white
        },
        toastColors: {
            red: {
                background: Colors.auvikRed[500],
                border: Colors.auvikRed[500],
                icon: black,
                progressBar: Colors.auvikRed[900],
                text: black
            },
            blue: {
                background: Colors.auvikCyan[500],
                border: Colors.auvikCyan[500],
                icon: black,
                progressBar: Colors.auvikCyan[900],
                text: black
            },
            green: {
                background: Colors.auvikGreen[500],
                border: Colors.auvikGreen[500],
                icon: black,
                progressBar: Colors.auvikGreen[900],
                text: black
            },
            yellow: {
                background: Colors.auvikYellow[500],
                border: Colors.auvikYellow[500],
                icon: black,
                progressBar: Colors.auvikYellow[900],
                text: black
            }
        },
        tooltips: {
            darkTooltip: {
                background: black,
                color: white,
                arrowColor: black
            },
            lightTooltip: {
                background: black,
                border: black,
                color: white,
                arrowColor: black
            },
            pieChart: {
                backgroundColor: black,
                headerRowBackgroundColor: eggplantGray[800]
            },
            tooltipQ: {
                color: white
            }
        },
        topInfoBanner: {
            defaultBackgroundColor: auvikPurple[500],
            defaultBorderColor: auvikPurple[500],
            configureIntegration: {
                backgroundColor: auvikCyan[500],
                borderColor: auvikCyan[500],
                textColor: auvikEggplant[500],
                titleColor: auvikEggplant[500]
            }
        },
        trialExpiryJailCard: {
            backgroundTrialJailGradientEnd: auvikLavender[900],
            backgroundTrialJailGradientStart: auvikEggplant[900],
            cardBackgroundColor: black,
            divider: auvikPurple[800],
            icon: white,
            logoutIcon: white,
            logoutText: white,
            subcardBorderColor: eggplantGray[800],
            subtitle: white,
            text: white,
            title: white
        },
        usage: {
            badgeIcon: {
                high: black,
                low: black,
                medium: black,
                waste: black
            },
            badgeTextColor: {
                high: black,
                low: black,
                medium: black,
                waste: black
            },
            icon: {
                high: auvikGreen[500],
                low: auvikYellow[500],
                medium: auvikLime[500],
                waste: auvikRed[500]
            },
            textColor: {
                high: auvikGreen[500],
                low: auvikYellow[500],
                medium: auvikLime[500],
                waste: auvikRed[500]
            },
            barChart: {
                'High Usage': auvikGreen[500],
                'Medium Usage': auvikLime[500],
                'Low Usage': auvikYellow[500],
                Waste: auvikRed[500]
            }
        },
        userInvitationStatus: {
            accepted: {
                icon: black,
                background: auvikGreen[500],
                borderColor: auvikGreen[500],
                text: black
            },
            pending: {
                icon: black,
                background: auvikYellow[500],
                borderColor: auvikYellow[500],
                text: black
            }
        },
        viewPricing: {
            dividerPerUser: auvikPurple[500],
            dividerExclusive: auvikPurple[800],
            icon: auvikPurple[500],
            textTitle: white,
            textSubtitle: white,
            textPerUser: white,
            textExclusive: white
        },
        warning: { main: auvikYellow[500] }
    },
    typography: {
        fontFamily: [
            '"Open Sans"',
            'Helvetica',
            'Arial',
            'Verdana',
            'sans-serif'
        ].join(','),
        fontSize: FontSizes[1],
        h1: {
            color: white,
            fontSize: FontSizes[5],
            fontWeight: 500,
            padding: 0,
            textTransform: 'capitalize'
        },
        h2: {
            color: white,
            fontSize: FontSizes[4],
            fontWeight: 500,
            padding: 0,
            textTransform: 'capitalize'
        },
        h3: {
            color: white,
            fontSize: FontSizes[4],
            fontWeight: 'normal',
            padding: 0,
            marginTop: '4px',
            marginBottom: '8px',
            textTransform: 'capitalize'
        },
        h4: {
            color: white,
            fontSize: FontSizes[3],
            textTransform: 'capitalize'
        },
        h5: {
            color: white,
            fontSize: FontSizes[3],
            textTransform: 'capitalize'
        },
        h6: {
            color: white,
            fontSize: FontSizes[2],
            textTransform: 'capitalize'
        },
        subtitle1: {
            color: white,
            fontSize: FontSizes[2],
            textTransform: 'capitalize'
        },
        subtitle2: {
            color: white,
            fontSize: FontSizes[2]
        },
        body1: {
            fontSize: FontSizes[1]
        },
        body2: {
            color: white,
            fontSize: FontSizes[1]
        },
        button: {
            fontSize: FontSizes[1],
            textTransform: 'uppercase'
        },
        codeBlock: {
            color: white,
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 600,
            whiteSpace: 'pre-line'
        },
        small: {
            fontSize: '11px'
        },
        span: {
            fontSize: FontSizes[1]
        }
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                a: {
                    color: white,
                    cursor: 'pointer',
                    textDecoration: 'none !important',
                    '&.link.underline': {
                        borderBottom: '1px solid transparent',
                        color: white,
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.bold.link.underline': {
                        borderBottom: '1px solid transparent',
                        color: white,
                        fontWeight: 600,
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link': {
                        color: white,
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--secondary': {
                        color: auvikLavender[500],
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--tertiary': {
                        color: auvikPurple[500],
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--white': {
                        '&:hover': {
                            color: black
                        }
                    },
                    '&.link--errorAlert': {
                        borderBottom: '1px solid transparent',
                        color: auvikEggplant[500],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link--infoAlert': {
                        borderBottom: '1px solid transparent',
                        color: auvikEggplant[500],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.showLink': {
                        '@media print': {
                            color: auvikEggplant[500],
                            textDecoration: 'underline !important'
                        }
                    }
                },
                code: {
                    fontFamily: "'Courier New', Courier, monospace !important",
                    fontWeight: 600,
                    backgroundColor: auvikEggplant[500],
                    color: white,
                    padding: '4px',
                    borderRadius: '4px',
                    fontSize: '13px'
                },
                html: {
                    height: '100%'
                },
                input: {
                    '&.link': {
                        color: white,
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    }
                },
                li: {
                    '&.li-with-spacing': {
                        lineHeight: '24px',
                        marginBottom: '4px',
                        marginTop: '4px'
                    },
                    '&.li-purple': {
                        color: white,
                        display: 'list-item',
                        listStyleType: 'disc',
                        paddingLeft: '0px',
                        '&::marker': {
                            color: auvikPurple[500],
                            fontSize: '16px'
                        }
                    },
                    // Markdown styles
                    '&.markdown-li': {
                        lineHeight: '24px',
                        marginBottom: '12px',
                        '> p': { marginBottom: 0 },
                        '&::marker': {
                            color: auvikPurple[500],
                            fontSize: '20px'
                        },
                        '& > ul > li': {
                            listStyleType: 'disc',
                            marginBottom: 0,
                            '&::marker': {
                                color: auvikPurple[100],
                                fontSize: '20px'
                            }
                        }
                    }
                },
                ul: {
                    '&.markdown-ul': {
                        marginBottom: 0,
                        paddingBottom: '8px',
                        paddingLeft: '20px'
                    }
                },
                span: {
                    '&.link--errorAlert': {
                        color: auvikEggplant[500],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link': {
                        '&:hover': {
                            color: auvikPurple[500],
                            textDecoration: 'underline'
                        },
                        cursor: 'pointer'
                    }
                }
            }
        },
        // Name of the component
        MuiAccordion: {
            styleOverrides: {
                // Name of the slot
                root: {
                    // Some CSS
                    backgroundColor: eggplantGray[800],
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    border: `1px solid ${lavenderGray[500]}`,
                    boxShadow: 'none'
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    borderTop: `1px solid ${lavenderGray[500]}`,
                    paddingTop: '20px'
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'info' && {
                            backgroundColor: auvikCyan[500],
                            border: `1px solid ${auvikCyan[500]}`,
                            color: auvikEggplant[500]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'success' && {
                            backgroundColor: auvikGreen[500],
                            border: `1px solid ${auvikGreen[500]}`,
                            color: auvikEggplant[500]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'warning' && {
                            backgroundColor: auvikYellow[500],
                            border: `1px solid ${auvikYellow[500]}`,
                            color: auvikEggplant[500]
                        }),
                    ...(ownerState.variant === 'outlined' &&
                        ownerState.severity === 'warning' && {
                            border: `1px solid ${auvikYellow[500]}`,
                            color: auvikEggplant[500]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'error' && {
                            backgroundColor: auvikRed[500],
                            border: `1px solid ${auvikRed[500]}`,
                            color: auvikEggplant[500]
                        }),
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    }
                }),
                icon: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.severity === 'info' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'success' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'warning' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'error' && {
                        color: auvikEggplant[500]
                    }),
                    paddingLeft: '5px',
                    marginRight: '7px'
                }),
                message: ({ ownerState }: { ownerState: OwnerState }) => ({
                    alignSelf: 'center',
                    ...(ownerState.severity === 'info' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'success' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'warning' && {
                        color: auvikEggplant[500]
                    }),
                    ...(ownerState.severity === 'error' && {
                        color: auvikEggplant[500]
                    }),
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    }
                }),
                action: {
                    alignItems: 'center',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                }
            }
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    fontWeight: 600
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                input: {
                    color: white
                },
                option: {
                    color: white,
                    '&[aria-selected="true"]': {
                        backgroundColor: `${auvikPurple[500]} !important`,
                        '&:hover': {
                            backgroundColor: `${auvikPurple[500]} !important`
                        }
                    }
                },
                popper: {
                    width: 'unset',
                    '& .MuiAutocomplete-option:hover': {
                        backgroundColor: `${eggplantGray[800]} !important`
                    }
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[0],
                    width: 'fit-content',
                    borderRadius: '90px',
                    height: '24px',
                    minWidth: '24px'
                }
            }
        },
        MuiAvatarGroup: {
            styleOverrides: {
                root: {
                    '& .MuiAvatar-root': {
                        border: `2px solid ${eggplantGray[800]}`
                    },
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                avatar: {
                    backgroundColor: auvikCyan[500],
                    borderColor: 'transparent',
                    borderRadius: '24px',
                    color: black,
                    fontSize: FontSizes[0],
                    fontWeight: 600,
                    height: '24px',
                    marginLeft: 0,
                    minWidth: '24px',
                    paddingLeft: '2px',
                    paddingRight: '2px',
                    width: 'auto'
                }
            }
        },
        MuiBadge: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                badge: {
                    '&:not(.MuiBadge-dot, .MuiBadge-newDot)': {
                        minWidth: '16px',
                        width: '16px',
                        height: '16px'
                    },
                    zIndex: 0
                },
                dot: {
                    backgroundColor: auvikPurple[500]
                },
                newDot: {
                    minWidth: '8px',
                    height: '8px',
                    marginTop: '4px',
                    padding: '0',
                    borderRadius: '4px',
                    backgroundColor: auvikYellow[500]
                },
                standard: {
                    backgroundColor: black,
                    color: white
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    textTransform: 'uppercase',
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    },
                    '&.MuiButton-contained': {
                        '&:disabled': {
                            cursor: 'not-allowed',
                            pointerEvents: 'all'
                        }
                    },
                    '&.MuiButton-containedPrimary': {
                        backgroundColor: auvikLavender[800],
                        border: `1px solid ${auvikLavender[800]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikLavender[800],
                            border: `1px solid ${auvikLavender[800]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikLavender[900],
                            border: `1px solid ${auvikLavender[900]}`,
                            color: white
                        },
                        ...(ownerState.size === 'slim' && {
                            fontSize: '12px',
                            padding: '2px 10px'
                        })
                    },
                    '&.MuiButton-containedSecondary': {
                        backgroundColor: gray[300],
                        border: `1px solid ${gray[300]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            color: white
                        }
                    },
                    '&.MuiButton-containedDanger': {
                        backgroundColor: auvikRed[500],
                        border: `1px solid ${auvikRed[500]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikRed[900],
                            border: `1px solid ${auvikRed[900]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikRed[900],
                            border: `1px solid ${auvikRed[900]}`,
                            color: white
                        }
                    },
                    '&.MuiButton-iconWithoutBorder': {
                        // Disabled state has to override hover state
                        backgroundColor: 'transparent',
                        borderRadius: '50%',
                        color: gray[100],
                        '&&:disabled': {
                            color: lavenderGray[500],
                            cursor: 'not-allowed'
                        },

                        '&:focus-visible': {
                            backgroundColor: 'rgba(0, 0, 0, 0.15)'
                        },

                        '&:hover': {
                            textDecoration: 'none',
                            color: white
                        }
                    },
                    '&.MuiButton-outlinedPrimary': {
                        color: white,
                        border: `1px solid ${white}`,
                        boxShadow: 'none',
                        backgroundColor: eggplantGray[800],
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            color: eggplantGray[500],
                            backgroundColor: eggplantGray[700],
                            border: `1px solid ${eggplantGray[500]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikLavender[800],
                            border: `1px solid ${auvikLavender[800]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikLavender[900],
                            border: `1px solid ${auvikLavender[900]}`,
                            color: white
                        },

                        ...(ownerState.size === 'slim' && {
                            fontSize: '12px',
                            padding: '2px 10px'
                        })
                    },
                    '&.MuiButton-outlinedTransparentPrimary': {
                        color: white,
                        border: `1px solid ${white}`,
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            color: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikLavender[800],
                            border: `1px solid ${auvikLavender[800]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikLavender[900],
                            border: `1px solid ${auvikLavender[900]}`,
                            color: white
                        }
                    },
                    '&.MuiButton-outlinedSecondary': {
                        color: gray[300],
                        border: `1px solid ${gray[300]}`,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        backgroundColor: white,
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            border: `1px solid ${lavenderGray[700]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            border: `1px solid ${lavenderGray[500]}`,
                            color: lavenderGray[500]
                        },

                        '&:hover': {
                            border: `1px solid ${lavenderGray[500]}`,
                            color: lavenderGray[500]
                        }
                    },

                    '&.MuiButton-textPrimary': {
                        color: auvikLavender[500],
                        '&&:disabled': {
                            color: lavenderGray[600],
                            cursor: 'not-allowed'
                        },
                        '&:focus': {
                            color: auvikLavender[800]
                        },
                        '&:hover': {
                            color: auvikLavender[800]
                        }
                    },
                    minWidth: '64px',
                    lineHeight: '1.75',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                    padding: '4px 16px',
                    '&.MuiButton-sizeAnmSmall': {
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'normal',
                        minWidth: '50px',
                        padding: '4px 10px'
                    },
                    '&.MuiButton-sizeAnmMedium': {
                        borderRadius: '4px',
                        fontSize: '13px',
                        minWidth: '170px',
                        padding: '8px 18px'
                        // padding: '4px 16px' // To match current MUI
                    },
                    '&.MuiButton-sizeAnmLarge': {
                        borderRadius: '4px',
                        fontSize: '13px',
                        minWidth: '200px',
                        padding: '12px 18px'
                    }
                })
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    },
                    '&.MuiIconButton-root': {
                        height: '36px',
                        width: '36px',
                        ...(ownerState.variant === 'slim' && {
                            height: '24px',
                            padding: 0,
                            width: '24px'
                        }),
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            color: lavenderGray[600],
                            cursor: 'not-allowed'
                        },
                        '&:focus': {
                            backgroundColor: black,
                            color: auvikLavender[800]
                        },
                        '&:hover': {
                            color: auvikLavender[900],
                            textDecoration: 'none'
                        },
                        backgroundColor: 'transparent',
                        borderRadius: '50%',
                        '&.MuiAutocomplete-clearIndicator.MuiIconButton-root': {
                            color: white
                        },
                        '&.MuiAutocomplete-popupIndicator.MuiIconButton-root': {
                            color: white
                        }
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600,
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white,
                            fontWeight: 600
                        }
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root:not(.Mui-selected)': {
                        borderColor: auvikPurple[500]
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': {
                        color: gray[300]
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root:hover, &.MuiPickersDay-root:focus': {
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600
                    },
                    '&.MuiPickersArrowSwitcher-button': {
                        color: white
                    },
                    '&.MuiPickersCalendarHeader-switchViewButton': {
                        color: white
                    },
                    '&.MuiChip-root': {
                        backgroundColor: auvikLavender[500]
                    },
                    '&.MuiRadio-root.Mui-checked ': {
                        color: auvikLavender[500]
                    }
                })
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    textTransform: 'capitalize',
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    textAlign: 'left',
                    '& .MuiTypography-h5': {
                        color: white,
                        fontSize: FontSizes[1]
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'secondary' && {
                        '&.Mui-checked': {
                            color: auvikPurple[500]
                        }
                    }),
                    ...(ownerState.variant === 'tertiary' && {
                        '&.Mui-checked': {
                            color: auvikLavender[800]
                        }
                    }),
                    ...(ownerState.variant !== 'secondary' &&
                        ownerState.variant !== 'tertiary' && {
                            '&.Mui-checked': {
                                color: auvikLavender[800]
                            }
                        }),
                    color: white,
                    '&.Mui-disabled': {
                        color: gray[300]
                    }
                })
            }
        },
        MuiChip: {
            styleOverrides: {
                label: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'whiteText' && {
                        color: white
                    }),
                    ...(ownerState.variant === 'blackText' && {
                        color: black
                    }),
                    ...(ownerState.variant !== 'whiteText' &&
                        ownerState.variant !== 'blackText' && {
                            color: black
                        })
                }),
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    backgroundColor: black,
                    ...(ownerState.variant === 'secondary' && {
                        backgroundColor: auvikLavender[500],
                        color: white,
                        '& .MuiChip-label': {
                            color: white
                        }
                    }),
                    ...(ownerState.variant === 'tertiary' && {
                        backgroundColor: auvikPurple[500]
                    }),
                    ...(ownerState.variant === 'rank' && {
                        backgroundColor: auvikCyan[500],
                        color: black
                    })
                })
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                paperFullScreen: {
                    marginLeft: SIDEBAR_WIDTH
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    backgroundColor: eggplantGray[800]
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    backgroundColor: eggplantGray[800]
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[4]
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                label: {
                    color: white
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: white,
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    // icons at the end of the calendar picker
                    '& .MuiButtonBase-root.MuiIconButton-root': {
                        color: white
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: white,
                    '&.MuiInput-underline:before': {
                        borderBottomColor: lavenderGray[500]
                    },
                    '&.MuiInput-underline:after': {
                        borderBottom: `1px solid ${lavenderGray[500]}`
                    },
                    '&.MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                            borderColor: auvikSkyBlue[500],
                            boxShadow:
                                'rgb(0 0 0 / 8%) 0px 1px 1px inset, rgb(102 175 233 / 60%) 0px 0px 8px'
                        },
                        border: 'none',
                        backgroundColor: eggplantGray[700]
                    },

                    '&.MuiOutlinedInput-root.Mui-disabled': {
                        border: 'none'
                    },
                    '& .MuiChip-label': {
                        color: white
                    },
                    '& .MuiChip-deleteIcon': {
                        fill: white
                    }
                }
            }
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.color === 'success' && {
                        backgroundColor: eggplantGray[600]
                    }),
                    '&.MuiLinearProgress-colorPrimary': {
                        backgroundColor: black
                    }
                }),
                bar: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.color === 'success' && {
                        backgroundColor: auvikGreen[500]
                    }),
                    '&.MuiLinearProgress-barColorPrimary': {
                        backgroundColor: auvikLavender[500]
                    }
                })
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: auvikLavender[500],
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:hover': {
                        color: auvikPurple[500]
                    }
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    backgroundColor: 'unset'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: white,
                    '&:hover': {
                        backgroundColor: eggplantGray[800]
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: auvikPurple[500],
                        color: white
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.Mui-selected': {
                        '&:hover': {
                            backgroundColor: auvikPurple[500]
                        },
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.Mui-selected.Mui-focusVisible': {
                        '&:hover': {
                            backgroundColor: auvikPurple[500]
                        },
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600
                    }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'outlined' && {
                        border: '1px solid',
                        borderColor: eggplantGray[800],
                        borderRadius: '4px'
                    }),
                    backgroundColor: eggplantGray[800],
                    color: white,
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    padding: '8px',
                    '&.MuiMenu-paper': {
                        backgroundColor: black,
                        elevation: 24
                    },
                    '&.MuiAutocomplete-paper': {
                        backgroundColor: black
                    }
                })
            }
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled:not(.Mui-selected)': {
                        color: gray[300],
                        fontWeight: 300
                    }
                }
            }
        },
        MuiPickersMonth: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: auvikRed[500],
                    '& .MuiPickersMonth-monthButton': {
                        fontSize: FontSizes[1],
                        color: white,
                        '&.Mui-selected': {
                            backgroundColor: auvikPurple[500],
                            '&:hover, &:focus': {
                                backgroundColor: auvikPurple[500],
                                color: white,
                                fontWeight: 600
                            }
                        },
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '&.Mui-disabled:not(.Mui-selected)': {
                            color: gray[300],
                            fontWeight: 300
                        }
                    }
                }
            }
        },
        MuiPickersYear: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: auvikRed[500],
                    '& .MuiPickersYear-yearButton': {
                        fontSize: FontSizes[1],
                        color: white,
                        '&.Mui-selected': {
                            backgroundColor: auvikPurple[500],
                            fontWeight: 600,
                            '&:hover, &:focus': {
                                backgroundColor: auvikPurple[500],
                                color: white,
                                fontWeight: 600
                            }
                        },
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '&.Mui-disabled:not(.Mui-selected)': {
                            color: gray[300],
                            fontWeight: 300
                        }
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.className === 'asmTenantSelector' && {
                        backgroundColor: eggplantGray[400],
                        border: `1px solid ${auvikLavender[500]}`,
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        height: '32px',
                        lineHeight: '24px',
                        color: white,
                        width: '100%',
                        '> .MuiSelect-icon': {
                            color: white,
                            height: '22px',
                            width: '33px',
                            ':not(.MuiSelect-iconOpen)': {
                                borderLeft: `1px solid ${lavenderGray[500]}`,
                                paddingLeft: '8px',
                                right: '8px'
                            },
                            '&.MuiSelect-iconOpen': {
                                borderRight: `1px solid ${lavenderGray[500]}`,
                                paddingRight: '8px',
                                right: '8px'
                            }
                        },
                        '> .MuiSelect-standard.MuiInputBase-input.MuiSelect-select.MuiInput-input':
                            {
                                paddingLeft: '9px', // makes the text inside the select align with the sidebar icons below it.
                                paddingRight: '42px' // 24px (standard padding) + 18px (additional padding to keep text from overflowing the chevron area)
                            },
                        ':before': {
                            border: 'none'
                        }
                    }),
                    backgroundColor: eggplantGray[800]
                })
            }
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    '&.Mui-completed': {
                        color: auvikPurple[500]
                    }
                }
            }
        },
        MuiStepLabel: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: white
                },
                label: {
                    color: white,
                    '&.Mui-active': {
                        color: white
                    },
                    '&.Mui-completed': {
                        color: white
                    }
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    '&.MuiNativeSelect-icon': {
                        color: white
                    },
                    '&.MuiSelect-icon': {
                        color: white
                    }
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[2],
                    backgroundColor: eggplantGray[700],
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    marginLeft: '2px',
                    marginRight: '2px',
                    textTransform: 'capitalize',
                    '&.MuiTab-textColorSecondary.Mui-selected': {
                        backgroundColor: eggplantGray[800],
                        fontWeight: 600,
                        color: white
                    },
                    '&.MuiTab-textColorSecondary': {
                        fontWeight: 'normal',
                        color: white
                    },
                    '&.MuiTab-textColorPrimary.Mui-selected': {
                        backgroundColor: eggplantGray[800],
                        fontWeight: 600,
                        color: white
                    },
                    '&.MuiTab-textColorPrimary': {
                        fontWeight: 'normal',
                        color: white
                    },
                    ...(ownerState.className === 'nestedTabs' && {
                        backgroundColor: eggplantGray[900],
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        marginLeft: 0,
                        marginRight: 0,
                        '&.MuiTab-textColorPrimary.Mui-selected': {
                            fontWeight: 600
                        }
                    })
                }),
                wrapper: {
                    fontSize: FontSizes[2]
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                indicator: ({ ownerState }: { ownerState: OwnerState }) => ({
                    display: 'none',
                    ...(ownerState.className === 'nestedTabs' && {
                        display: 'unset',
                        backgroundColor: white,
                        height: '4px'
                    })
                })
            }
        },
        MuiTable: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'alternateRows' && {
                        '& tbody tr:nth-of-type(2n)': {
                            backgroundColor: `${black} !important`,
                            '&:hover': {
                                backgroundColor: `${auvikPurple[900]} !important`
                            }
                        }
                    })
                })
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: white,
                    borderBottom: `1px solid ${black}`
                }
            }
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    overflowY: 'hidden'
                },
                actions: {
                    color: white,
                    '&:hover': {
                        color: auvikPurple[500]
                    },
                    '& .MuiIconButton-root': {
                        '&:focus': {
                            backgroundColor: 'transparent',
                            color: lightGray[200]
                        },
                        '&:hover': {
                            backgroundColor: auvikPurple[50],
                            color: gray[700]
                        },
                        color: lightGray[200]
                    },
                    '& .Mui-disabled': {
                        color: `${gray[300]} !important`
                    }
                },
                caption: {
                    color: white
                },
                displayedRows: {
                    margin: 0,
                    color: white
                },
                selectLabel: {
                    margin: 0,
                    color: white
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant !== 'noHover' && {
                        '&:hover': {
                            backgroundColor: auvikPurple[900]
                        }
                    })
                })
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard'
            },
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                input: {
                    '&::placeholder': {
                        color: lightGray[200],
                        opacity: 1
                    }
                }
            }
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    backgroundColor: eggplantGray[800],
                    borderColor: white,
                    color: white,
                    '&:hover': {
                        backgroundColor: auvikLavender[900],
                        borderColor: auvikLavender[900],
                        color: white
                    },
                    '&.Mui-selected': {
                        backgroundColor: auvikLavender[900],
                        borderColor: auvikLavender[900],
                        color: white
                    },
                    '&:focus': {
                        borderColor: auvikSkyBlue[500],
                        boxShadow:
                            'rgb(0 0 0 / 8%) 0px 1px 1px inset, rgb(102 175 233 / 60%) 0px 0px 8px'
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    background: 'none'
                }
            }
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    span: 'span'
                }
            }
        },
        // Dashboard panal styles
        Panel: {
            title: {
                // Variants for the panel title
                large: { fontSize: FontSizes[3], fontWeight: 600 },
                medium: { fontSize: FontSizes[2], fontWeight: 600 },
                small: { fontSize: FontSizes[1], fontWeight: 'normal' }
            }
        }
    }
};
